import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useParentAuth } from "@/context/ParentAuthContext";
import { toast } from "sonner";
import { localReturnPath } from "@/lib/api";
import { ArrowRight, ShieldCheck, User, LogIn } from "lucide-react";

const TABS = [
  { k: "parent", label: "Parent" },
  { k: "organizer", label: "Organizer" },
];

export default function SignIn() {
  const [params] = useSearchParams();
  const initialTab = params.get("tab") === "organizer" ? "organizer" : "parent";
  const [tab, setTab] = useState(initialTab);
  const { parent } = useParentAuth();
  const navigate = useNavigate();

  const next = localReturnPath(params.get("next"));
  useEffect(() => {
    if (parent && tab === "parent") navigate(next, { replace: true });
  }, [parent, tab, navigate, next]);

  return (
    <div className="max-w-xl mx-auto px-5 py-16 md:py-24">
      <span className="text-xs uppercase tracking-wider font-semibold text-[#FF9F1C]">Sign in</span>
      <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl tracking-tight leading-none">Welcome back.</h1>
      <p className="mt-3 text-[#7A7D8C]">Choose your role and pick up where you left off.</p>

      <div className="mt-8 flex gap-2 p-1 bg-white rounded-full border border-slate-200 w-fit" data-testid="signin-tabs">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`signin-tab-${t.k}`}
            className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors ${tab === t.k ? "bg-[#FF9F1C] text-white shadow-[0_6px_18px_rgb(255,159,28,0.3)]" : "text-[#1E1E24] hover:bg-[#FFBF69]/15"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "parent" ? <ParentPanel /> : <OrganizerPanel />}
      </div>
    </div>
  );
}

function ParentPanel() {
  const { login, register } = useParentAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signin") {
        await login(f.email.trim().toLowerCase(), f.password);
        toast.success("Signed in");
      } else {
        const res = await register(f.name.trim(), f.email.trim().toLowerCase(), f.password);
        const linked = res?.linked_existing_cubers || 0;
        if (linked > 0) {
          toast.success(`Welcome! We found ${linked} past ${linked === 1 ? "entry" : "entries"} and linked them to your account.`);
        } else {
          toast.success("Welcome to Naptown Cubers!");
        }
      }
      navigate(localReturnPath(params.get("next")));
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not " + (mode === "signin" ? "sign in" : "create account"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8" data-testid="parent-panel">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[#FFBF69]/25 grid place-items-center text-[#B36906]"><User className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display font-black text-xl">Parent account</h2>
          <p className="text-xs text-[#7A7D8C]">See your cuber's results after each competition.</p>
        </div>
      </div>

      <div className="mt-6 inline-flex text-xs font-semibold rounded-full bg-[#FDFFFC] border border-slate-100 p-0.5">
        {[
          { k: "signin", label: "Sign in" },
          { k: "signup", label: "Create account" },
        ].map((m) => (
          <button key={m.k} onClick={() => setMode(m.k)} data-testid={`parent-mode-${m.k}`} className={`px-4 py-1.5 rounded-full transition-colors ${mode === m.k ? "bg-[#FF9F1C] text-white" : "text-[#1E1E24] hover:bg-[#FFBF69]/15"}`}>{m.label}</button>
        ))}
      </div>

      <form onSubmit={submit} className="mt-5 space-y-4" data-testid="parent-form">
        {mode === "signup" && (
          <Field label="Your name" value={f.name} onChange={(v) => setF({ ...f, name: v })} required tid="parent-name" />
        )}
        <Field label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} required tid="parent-email" />
        <Field label={mode === "signup" ? "Password (8+ characters)" : "Password"} type="password" value={f.password} onChange={(v) => setF({ ...f, password: v })} required tid="parent-password" />
        {mode === "signin" && (
          <div className="text-right">
            <Link to="/forgot-password" data-testid="forgot-link" className="text-xs font-semibold text-[#FF9F1C]">Forgot password?</Link>
          </div>
        )}
        <button disabled={busy} type="submit" data-testid="parent-submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#FF9F1C] text-white font-bold shadow-[0_12px_30px_rgb(255,159,28,0.35)] hover:-translate-y-0.5 transition-transform disabled:opacity-60">
          {busy ? "…" : mode === "signin" ? "Sign in" : "Create account"} <ArrowRight className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}

function OrganizerPanel() {
  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-8" data-testid="organizer-panel">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-[#FF9F1C] grid place-items-center text-white"><ShieldCheck className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display font-black text-xl">Organizer console</h2>
          <p className="text-xs text-[#7A7D8C]">For judges and organizers running competitions.</p>
        </div>
      </div>

      <p className="mt-6 text-sm text-[#7A7D8C]">Sign in with your approved Google account to manage competitors, record solves, and publish results.</p>

      <button onClick={startLogin} data-testid="google-signin-btn" className="mt-6 w-full flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white py-3.5 font-semibold hover:border-[#FF9F1C] hover:-translate-y-0.5 transition-transform">
        <GoogleIcon /> Continue with Google <LogIn className="w-4 h-4" />
      </button>

      <p className="mt-4 text-[11px] text-[#7A7D8C]">Access is limited to invited organizers and judges. Not on the list? <Link to="/contact" className="text-[#FF9F1C] font-semibold">Say hi</Link>.</p>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}{required && <span className="text-[#E71D36]"> *</span>}</label>
      <input data-testid={tid} type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C] focus:border-[#FF9F1C]" />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
